import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import Button from "../Button/Button";
import Badge from "../Badge/Badge";
import VehicleApi from "../../../../passapp-sdk/vehicle";
import { useStore } from "../../../../hooks/useStore";
import useBreakpoint from "../../../../hooks/useBreakpoint";
import useShowImage from "../../../../hooks/useShowImage";
import PhotoThumbnailStrip, { StripPhoto } from "../PicturePreview/PhotoThumbnailStrip";
import useInsuranceCompanies from "../../../../hooks/useInsuranceCompanies";
import { getEntryTransportAuthorization } from "../Table/utils/entryAuthorization";
import { formatTransportLocation } from "../../../../utils/constants/locationConstants";
import {
  formatManualAuthorizer,
  getTransportAuthorizer,
} from "../Table/utils/getTransportAuthorizer";
import { formatGuardiaFecha } from "../Table/utils/formatDate";
import { driverName, eventUserId, VehicleInsideRow } from "../Table/utils/vehicleInsideRow";
import type { EditDockTarget } from "./EditAssignedDockDialog";
import "./VehicleDetailDialog.css";

const vehicleApi = new VehicleApi();

type VehicleDetailDialogProps = {
  open: boolean;
  row: VehicleInsideRow | null;
  onClose: () => void;
  onEditDock?: (target: EditDockTarget) => void;
};

const Dato: React.FC<{ label: string; wide?: boolean }> = ({ label, wide, children }) => {
  const vacio = children === undefined || children === null || children === "";
  return (
    <div className={wide ? "vd-dato vd-dato-wide" : "vd-dato"}>
      <label>{label}</label>
      <div>{vacio ? "—" : children}</div>
    </div>
  );
};

const Seccion: React.FC<{ num: number; titulo: string }> = ({ num, titulo, children }) => (
  <section className="vd-sec">
    <header className="vd-sec-head">
      <span className="vd-sec-num">{num}</span>
      <h3>{titulo}</h3>
    </header>
    {children}
  </section>
);

/**
 * Detalle de una unidad dentro del complejo.
 *
 * `getVehiclesInside` devuelve el evento entero vinculado a la unidad, así que casi todo se lee de
 * `row.event` sin pedir nada. Lo único que falta es la ficha del vehículo (VTV, póliza,
 * aseguradora), que se pide una sola vez al abrir y sólo si hay id de usuario.
 */
const VehicleDetailDialog: React.FC<VehicleDetailDialogProps> = ({
  open,
  row,
  onClose,
  onEditDock,
}) => {
  const { isMobile } = useBreakpoint();
  const { authStore } = useStore();
  const getImage = useShowImage();
  const { companies: insuranceCompanies } = useInsuranceCompanies();
  const [vehicleFile, setVehicleFile] = useState<any | null>(null);
  const [loadingVehicle, setLoadingVehicle] = useState(false);

  // Ficha del vehículo (VTV, póliza, aseguradora): son los únicos datos del bloque 1 que no viajan
  // en el evento. Si no se puede resolver el id del ingresante, no se pide y quedan en "—".
  const userId = row ? eventUserId(row) : undefined;

  const fetchVehicle = useCallback(async () => {
    if (!open || !row || !userId || !authStore.token) return;
    setLoadingVehicle(true);
    setVehicleFile(null);
    try {
      const response = await vehicleApi.getVehicles(authStore.token, userId);
      const list: any[] = Array.isArray(response?.data)
        ? response.data
        : Object.values(response?.data ?? {});
      const match = list.find((vehicle: any) => vehicle?.plate === row.plate);
      setVehicleFile(match ?? null);
    } catch (error) {
      console.error("Error fetching vehicle detail:", error);
    } finally {
      setLoadingVehicle(false);
    }
  }, [open, row, userId, authStore.token]);

  useEffect(() => {
    fetchVehicle();
  }, [fetchVehicle]);

  if (!row) return null;

  const event = row.event;
  // `getTransportAuthorizer` y `getEntryTransportAuthorization` leen `row.event`, así que se les
  // pasa un objeto con esa forma: el evento que devuelve getVehiclesInside es el mismo blob.
  const eventRow = { event } as any;
  const auth = getEntryTransportAuthorization(eventRow);
  const canEditDock = Boolean(row.eventId && auth);
  const eventVehicle = event?.vehicle;
  const host = (event?.invites ?? []).find((invite: any) => invite?.host)?.host;
  const authorizer = host ? undefined : getTransportAuthorizer(eventRow);

  const aseguradora =
    insuranceCompanies.find((company) => company.value === vehicleFile?.insuranceCompanyId)
      ?.label ?? vehicleFile?.insuranceCompany;

  const enganchadas = (row.associatedVehicles ?? [])
    .map((other) => `${other.plate} (${other.inside ? "dentro" : "fuera"})`)
    .join(" · ");

  const anfitrion = host
    ? [host.firstname, host.lastname].filter(Boolean).join(" ")
    : authorizer
    ? formatManualAuthorizer(authorizer)
    : undefined;

  const fotos: StripPhoto[] = [
    event?.photos?.observationsPhoto,
    event?.photos?.observationsPhoto2,
    event?.photos?.observationsPhoto3,
  ]
    .filter(Boolean)
    .map((id, index) => ({ url: getImage(id as string), label: `Observación ${index + 1}` }));

  // Sólo la trae la autorización de acceso; no viaja en el evento de ingreso.
  const enTransito = auth?.merchandiseInTransit;

  const { day, time } = formatGuardiaFecha(row.fecha);
  const subtitulo = [day && `Ingreso del ${day} ${time}`.trim(), row.transportCompanyName]
    .filter(Boolean)
    .join(" · ");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: "vd-paper" }}
    >
      <DialogTitle disableTypography className="vd-head">
        <div>
          <h2>{[row.vehicleTypeName, row.plate].filter(Boolean).join(" ") || "Unidad"}</h2>
          <p>{subtitulo}</p>
        </div>
        <IconButton onClick={onClose} aria-label="Cerrar" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="vd-body">
        <Seccion num={1} titulo="Vehículo">
          <div className="vd-grid3">
            <Dato label="Tipo de vehículo">{row.vehicleTypeName}</Dato>
            <Dato label="Patente">{row.plate}</Dato>
            <Dato label="Marca">{vehicleFile?.brand ?? eventVehicle?.brand}</Dato>
            <Dato label="Modelo">{vehicleFile?.model ?? eventVehicle?.model}</Dato>
            <Dato label="Color">{vehicleFile?.color ?? eventVehicle?.color}</Dato>
            <Dato label="Vencimiento VTV">
              {loadingVehicle ? "Cargando…" : vehicleFile?.technicalReviewExpirationDate}
            </Dato>
            <Dato label="Aseguradora">{loadingVehicle ? "Cargando…" : aseguradora}</Dato>
            <Dato label="Póliza n°">
              {loadingVehicle ? "Cargando…" : vehicleFile?.insurancePolicy}
            </Dato>
            <Dato label="Vencimiento del seguro">
              {loadingVehicle ? "Cargando…" : vehicleFile?.insuranceExpirationDate}
            </Dato>
            <Dato label="Unidades enganchadas" wide>
              {enganchadas || "Sin unidades enganchadas"}
            </Dato>
          </div>
        </Seccion>

        <Seccion num={2} titulo="Ingreso">
          <div className="vd-grid3">
            <Dato label="Ingresante / chofer">{driverName(row.driver)}</Dato>
            <Dato label="DNI">{row.driver?.dni}</Dato>
            <Dato label="Estado del chofer">
              {row.driverInside ? (
                <Badge label="Dentro del complejo" variant="green" />
              ) : (
                <Badge label="Fuera del complejo" variant="yellow" />
              )}
            </Dato>
            <Dato label="Empresa">{row.empresa}</Dato>
            <Dato label="Anfitrión">{anfitrion}</Dato>
            <Dato label="Dock asignado">
              <span className="vd-dock-value">
                {row.dock ?? "â€”"}
                {canEditDock && (
                  <Tooltip title="Editar dock asignado">
                    <IconButton
                      className="dock-edit-button"
                      aria-label="Editar dock asignado"
                      size="small"
                      onClick={() =>
                        onEditDock?.({
                          eventId: row.eventId,
                          nombre: driverName(row.driver) || "Transporte",
                          transportCompanyName: row.transportCompanyName,
                          patente: row.plate,
                          fecha: row.fecha,
                          dock: row.dock,
                        })
                      }
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </span>
            </Dato>
            <Dato label="Empresa de transporte">{row.transportCompanyName}</Dato>
            <Dato label="Procedencia">{formatTransportLocation(event?.transportOrigin)}</Dato>
            <Dato label="Estado de la unidad">
              <Badge label="Dentro del complejo" variant="green" />
            </Dato>
          </div>
        </Seccion>

        <Seccion num={3} titulo="Observaciones">
          <div className="vd-grid3">
            <Dato label="Número de remito">{event?.receipt}</Dato>
            <Dato label="Precinto">{event?.seal}</Dato>
            <Dato label="Pallets / bultos">
              {event?.pallets === null || event?.pallets === undefined
                ? undefined
                : String(event.pallets)}
            </Dato>
            {enTransito !== undefined && (
              <Dato label="Mercadería en tránsito">{enTransito ? "Sí" : "No"}</Dato>
            )}
            <Dato label="Notas del ingreso" wide>
              {event?.comment}
            </Dato>
            <Dato label="Fotos de observaciones" wide>
              <PhotoThumbnailStrip photos={fotos} size={72} emptyLabel="Sin foto adjunta" />
            </Dato>
          </div>
        </Seccion>
      </DialogContent>

      <DialogActions className="vd-foot">
        <Button variant="outline" onClick={onClose}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VehicleDetailDialog;
